import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TRecommendation } from '../../types/recommendation';
import { getMobileRecommendations, getDesktopRecommendations } from '../../services/getRecommendations';
import { ELoadingStatus } from '../../types/loadingStatus';
import { createAppAsyncThunk } from '../../utils/redux/createAppAsyncThunk';

const INITIAL_PAGE = 0;

export enum EPaginationStatus {
  Idle = 'idle',
  Loading = 'loading',
  Error = 'error',
}

type TRecommendationsState = {
  data: TRecommendation[];
  status: ELoadingStatus;
  hasRequested: boolean;
  page: number;
  hasMore: boolean;
  totalCount: number | null;
  paginationStatus: EPaginationStatus;
  favoriteUpdateRequestIds?: Record<number, string>;
};

const initialState: TRecommendationsState = {
  data: [],
  status: ELoadingStatus.Loading,
  hasRequested: false,
  page: INITIAL_PAGE,
  hasMore: false,
  totalCount: null as number | null,
  paginationStatus: EPaginationStatus.Idle,
  favoriteUpdateRequestIds: {},
};

export const fetchRecommendations = createAppAsyncThunk(
  'recommendations/fetch',
  (_, { getState, extra: context }) => {
    const {
      custom: { isMobile },
    } = context;
    const { regionId, landingType, liquiditySource } = getState();
    const parameters = {
      regionId,
      landingType,
      ...(liquiditySource ? { liquiditySource } : null),
      page: INITIAL_PAGE,
    };

    const getRecommendations = isMobile ? getMobileRecommendations : getDesktopRecommendations;

    return getRecommendations(context, { parameters, offset: INITIAL_PAGE });
  },
  {
    condition: (_, { getState }) => !getState().recommendations.hasRequested,
  },
);

export const fetchMoreRecommendations = createAppAsyncThunk(
  'recommendations/fetchMore',
  async (_, { getState, extra: context }) => {
    const {
      custom: { isMobile },
    } = context;
    const { regionId, landingType, liquiditySource, recommendations } = getState();
    const nextPage = recommendations.page + 1;
    const parameters = {
      regionId,
      landingType,
      page: nextPage,
      ...(liquiditySource ? { liquiditySource } : null),
    };
    const response = isMobile
      ? await getMobileRecommendations(context, {
          parameters,
          offset: recommendations.data.length,
        })
      : await getDesktopRecommendations(context, {
          parameters,
          offset: recommendations.data.length,
        });

    return {
      ...response,
      page: nextPage,
    };
  },
  {
    condition: (_, { getState }) => {
      const { hasMore, paginationStatus } = getState().recommendations;

      return hasMore && paginationStatus !== EPaginationStatus.Loading;
    },
  },
);

type TToggleFavoritePayload = {
  offerId: number;
  isFavorite: boolean;
};

type TFavoriteUpdatePayload = TToggleFavoritePayload & {
  requestId: string;
};

type TFinishFavoriteUpdatePayload = Pick<TFavoriteUpdatePayload, 'offerId' | 'requestId'>;

function updateFavorite(data: TRecommendation[], { offerId, isFavorite }: TToggleFavoritePayload): void {
  data.forEach(recommendation => {
    if (recommendation.offerId === offerId) {
      recommendation.isFavorite = isFavorite;
    }
  });
}

function setRecommendationPosition(recommendation: TRecommendation, position: number): TRecommendation {
  if (recommendation.analyticsData.type === 'trap' || recommendation.analyticsData.products[0].position === position) {
    return recommendation;
  }

  return {
    ...recommendation,
    analyticsData: {
      ...recommendation.analyticsData,
      products: [
        {
          ...recommendation.analyticsData.products[0],
          position,
        },
      ],
    },
  };
}

function appendUniqueRecommendations(
  currentRecommendations: TRecommendation[],
  addedRecommendations: TRecommendation[],
): TRecommendation[] {
  const offerIds = new Set<number>();
  const uniqueRecommendations: TRecommendation[] = [];

  currentRecommendations.forEach(({ offerId }) => {
    if (offerId !== null) {
      offerIds.add(offerId);
    }
  });

  addedRecommendations.forEach(recommendation => {
    const { offerId } = recommendation;

    if (offerId !== null) {
      if (offerIds.has(offerId)) {
        return;
      }

      offerIds.add(offerId);
    }

    uniqueRecommendations.push(
      setRecommendationPosition(recommendation, currentRecommendations.length + uniqueRecommendations.length + 1),
    );
  });

  return [...currentRecommendations, ...uniqueRecommendations];
}

const recommendationsSlice = createSlice({
  name: 'recommendations',
  reducers: {
    toggleFavorite: (state, action: PayloadAction<TToggleFavoritePayload>) => {
      updateFavorite(state.data, action.payload);

      if (state.favoriteUpdateRequestIds) {
        delete state.favoriteUpdateRequestIds[action.payload.offerId];
      }
    },
    startFavoriteUpdate: (state, action: PayloadAction<TFavoriteUpdatePayload>) => {
      const { offerId, requestId } = action.payload;

      updateFavorite(state.data, action.payload);
      state.favoriteUpdateRequestIds ??= {};
      state.favoriteUpdateRequestIds[offerId] = requestId;
    },
    rollbackFavoriteUpdate: (state, action: PayloadAction<TFavoriteUpdatePayload>) => {
      const { offerId, requestId } = action.payload;

      if (state.favoriteUpdateRequestIds?.[offerId] !== requestId) {
        return;
      }

      updateFavorite(state.data, action.payload);
      delete state.favoriteUpdateRequestIds[offerId];
    },
    finishFavoriteUpdate: (state, action: PayloadAction<TFinishFavoriteUpdatePayload>) => {
      const { offerId, requestId } = action.payload;

      if (state.favoriteUpdateRequestIds?.[offerId] === requestId) {
        delete state.favoriteUpdateRequestIds[offerId];
      }
    },
  },
  initialState,
  extraReducers(builder) {
    builder.addCase(fetchRecommendations.pending, state => {
      state.status = ELoadingStatus.Loading;
      state.hasRequested = true;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchRecommendations.fulfilled, (state, { payload }) => {
      state.status = ELoadingStatus.Success;
      state.hasRequested = true;
      state.data = appendUniqueRecommendations([], payload.recommendations);
      state.page = INITIAL_PAGE;
      state.hasMore = payload.hasMore;
      state.totalCount = payload.totalCount ?? null;
      state.paginationStatus = EPaginationStatus.Idle;
      state.favoriteUpdateRequestIds = {};
    });
    builder.addCase(fetchRecommendations.rejected, state => {
      state.status = ELoadingStatus.Failure;
      state.hasRequested = false;
      state.hasMore = false;
      state.totalCount = null;
      state.page = INITIAL_PAGE;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchMoreRecommendations.pending, state => {
      state.paginationStatus = EPaginationStatus.Loading;
    });
    builder.addCase(fetchMoreRecommendations.fulfilled, (state, { payload }) => {
      const recommendations = appendUniqueRecommendations(state.data, payload.recommendations);
      const hasNewRecommendations = recommendations.length > state.data.length;

      state.data = recommendations;
      state.page = payload.page;
      state.hasMore = hasNewRecommendations && payload.hasMore;
      state.totalCount = payload.totalCount ?? state.totalCount;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchMoreRecommendations.rejected, state => {
      state.paginationStatus = EPaginationStatus.Error;
    });
  },
});

export const recommendationsReducer = recommendationsSlice.reducer;
export const { finishFavoriteUpdate, rollbackFavoriteUpdate, startFavoriteUpdate, toggleFavorite } =
  recommendationsSlice.actions;
